// src/polaris-components/AdminProfilePage.tsx
import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { logoutAfterPasswordChange } from "../utils/passwordChangeLogout";
import DeleteAccountModal from "./DeleteAccountModal";
import ProfileForm from "./ProfileForm";
import { buildApiUrl } from "../utils/apiConfig";
import { getUserId, clearToken } from "../utils/auth";
import { requestAccountDeletion } from "../services/accountDeletionService";
import { useAuth } from "../contexts/AuthContext";
import "../styles/admin-profile-page.css";
import { apiClient } from "../utils/apiClient";

/**
 * Admin Profile Edit Page
 * Wraps ProfileForm to provide the correct URLs for editing the logged-in admin's profile
 */
export default function AdminProfilePage() {
    const navigate = useNavigate();
    const location = useLocation();
    const userId = getUserId();
    const { refreshAuth } = useAuth();

    // Check if this is from an invitation (first-time setup) - persist in state to survive re-renders
    const [fromInvitation] = useState(location.state?.fromInvitation === true);

    // Check if this is first-time setup (creating profile for the first time after login)
    const [firstTimeSetup] = useState(
        location.state?.firstTimeSetup === true || location.state?.createProfile === true,
    );

    // Check if password change is required
    const [requirePasswordChange] = useState(
        localStorage.getItem('requirePasswordChange') === 'true' ||
        location.state?.requirePasswordChange === true
    );

    // State for delete account modal
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState<string | null>(null);
    const [lastAdministrator, setLastAdministrator] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const response = await apiClient(buildApiUrl("api/admin-profile"), {
                    method: "GET",
                    headers: { Accept: "application/json" },
                    credentials: "include",
                });
                if (!response.ok) {
                    return;
                }
                const json = await response.json() as { lastAdministrator?: boolean };
                if (!cancelled) {
                    setLastAdministrator(Boolean(json.lastAdministrator));
                }
            } catch {
                // Modal falls back to personal-account copy if the profile flag cannot be loaded.
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    // Handle account deletion
    const handleDeleteAccount = async () => {
        if (!userId) return;

        setIsDeleting(true);
        setDeleteError(null);

        try {
            const result = await requestAccountDeletion(userId);

            if (result.success) {
                // Clear authentication and redirect to login
                clearToken();
                localStorage.clear();
                navigate('/login', {
                    replace: true,
                    state: {
                        message: lastAdministrator
                            ? 'Your organisation deletion request has been submitted. Everyone in the organisation has lost access. Data will be retained for up to 90 days, plus 30 days for reactivation.'
                            : 'Your account deletion request has been submitted. The account will be deleted in 30 days.'
                    }
                });
            } else {
                setDeleteError(result.error || 'Failed to delete account');
                setIsDeleting(false);
            }
        } catch (error) {
            setDeleteError(error instanceof Error ? error.message : 'An unexpected error occurred');
            setIsDeleting(false);
        }
    };

    // Handle successful password change
    const handlePasswordChanged = async () => {
        await logoutAfterPasswordChange();
    };

    // Handle profile save to refresh auth context
    const handleProfileSaved = async () => {
        // Refresh auth context to update hasProfile flag
        await refreshAuth();
    };

    // Always editing own profile on this route — set before ProfileForm renders (not only in useEffect).
    if (userId) {
        localStorage.setItem("loggedInUserEdit", "true");
    }

    // Redirect to login if userId is invalid
    useEffect(() => {
        if (!userId) {
            navigate('/login', { replace: true });
        }
    }, [userId, navigate]);

    // Don't render ProfileForm if userId is invalid
    if (!userId) {
        return null;
    }

    return (
        <div className="admin-profile-page-container">

            {requirePasswordChange && (
                <div className="alert alert-warning" style={{
                    backgroundColor: '#fff3cd',
                    border: '1px solid #ffc107',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px',
                    color: '#856404'
                }}>
                    <strong>⚠️ Password Change Required</strong>
                    <p style={{ margin: '8px 0 0 0' }}>
                        You must change your password before accessing other parts of the application.
                        Please update your password in the form below.
                    </p>
                </div>
            )}

            {deleteError && (
                <div className="alert alert-danger" style={{
                    backgroundColor: '#fee2e2',
                    border: '1px solid #dc2626',
                    borderRadius: '8px',
                    padding: '16px',
                    marginBottom: '20px',
                    color: '#991b1b'
                }}>
                    <strong>Error</strong>
                    <p style={{ margin: '8px 0 0 0' }}>
                        {deleteError}
                    </p>
                </div>
            )}

            <ProfileForm
                loadUrl={buildApiUrl('api/admin-profile')}
                saveUrl={buildApiUrl(`api/admin-profile`)}
                useCookies={false}
                afterSaveTo={(requirePasswordChange || fromInvitation) ? undefined : "/admin-profile"}
                onClose={(requirePasswordChange || fromInvitation || firstTimeSetup) ? undefined : () => navigate("/admin-profile")}
                onPasswordChanged={handlePasswordChanged}
                onSaved={handleProfileSaved}
                requirePasswordChange={requirePasswordChange}
                showDeleteAccount={true}
                onDeleteAccount={() => setShowDeleteModal(true)}
                firstTimeSetup={firstTimeSetup}
            />

            <DeleteAccountModal
                open={showDeleteModal}
                lastAdministrator={lastAdministrator}
                onClose={() => {
                    if (!isDeleting) {
                        setShowDeleteModal(false);
                        setDeleteError(null);
                    }
                }}
                onConfirm={handleDeleteAccount}
                loading={isDeleting}
            />
        </div>
    );
}

